import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ProjectCategory } from '../../model/types';
import type { InstanceProperties } from '../../services/instance-properties/types';
import type { AllProjectDetailsUpdate, ProjectDetails } from '../../services/project/types';

export const FETCH_PROJECT_DETAILS = 'state.entities.FETCH_PROJECT_DETAILS' as const;
export const FETCH_PROJECT_DETAILS_SUCCESS =
	'state.entities.FETCH_PROJECT_DETAILS_SUCCESS' as const;
export const FETCH_PROJECT_DETAILS_FAIL = 'state.entities.FETCH_PROJECT_DETAILS_FAIL' as const;

export const SAVE_PROJECT_DETAILS = 'state.entities.SAVE_PROJECT_DETAILS' as const;
export const SAVE_ALL_PROJECT_DETAILS = 'state.entities.SAVE_ALL_PROJECT_DETAILS' as const;
export const SAVE_PROJECT_DETAILS_SUCCESS = 'state.entities.SAVE_PROJECT_DETAILS_SUCCESS' as const;
export const SAVE_ALL_PROJECT_DETAILS_SUCCESS =
	'state.entities.SAVE_ALL_PROJECT_DETAILS_SUCCESS' as const;
export const SAVE_PROJECT_DETAILS_FAIL = 'state.entities.SAVE_PROJECT_DETAILS_FAIL' as const;

export const RELOAD_ON_REFRESH = 'state.entities.RELOAD_ON_REFRESH' as const;

export const FETCH_PROJECT_CATEGORIES = 'state.entities.FETCH_PROJECT_CATEGORIES' as const;

export const FETCH_PROJECT_CATEGORIES_SUCCESS =
	'state.entities.FETCH_PROJECT_CATEGORIES_SUCCESS' as const;

export const FETCH_PROJECT_CATEGORIES_FAIL =
	'state.entities.FETCH_PROJECT_CATEGORIES_FAIL' as const;

export const FETCH_INSTANCE_PROPERTIES = 'state.entities.FETCH_INSTANCE_PROPERTIES' as const;

export const FETCH_INSTANCE_PROPERTIES_SUCCESS =
	'state.entities.FETCH_INSTANCE_PROPERTIES_SUCCESS' as const;

export const FETCH_INSTANCE_PROPERTIES_FAIL =
	'state.entities.FETCH_INSTANCE_PROPERTIES_FAIL' as const;

export type FetchProjectDetailsAction = {
	type: typeof FETCH_PROJECT_DETAILS;
};

export const fetchProjectDetails = (): FetchProjectDetailsAction => ({
	type: FETCH_PROJECT_DETAILS,
});

export type FetchProjectDetailsSuccessAction = {
	type: typeof FETCH_PROJECT_DETAILS_SUCCESS;
	payload: ProjectDetails;
};

export const fetchProjectDetailsSuccess = (
	data: ProjectDetails,
): FetchProjectDetailsSuccessAction => ({
	type: FETCH_PROJECT_DETAILS_SUCCESS,
	payload: data,
});

export type FetchProjectDetailsFailAction = {
	type: typeof FETCH_PROJECT_DETAILS_FAIL;
	payload: {
		error: Error;
	};
};

export const fetchProjectDetailsFail = (error: Error): FetchProjectDetailsFailAction => ({
	type: FETCH_PROJECT_DETAILS_FAIL,
	payload: { error },
});

export type SaveProjectDetailsAction = {
	type: typeof SAVE_PROJECT_DETAILS;
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	};
};

export const saveProjectDetails = (analyticsEvent: UIAnalyticsEvent): SaveProjectDetailsAction => ({
	type: SAVE_PROJECT_DETAILS,
	meta: { analyticsEvent },
});

export type SaveAllProjectDetailsAction = {
	type: typeof SAVE_ALL_PROJECT_DETAILS;
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	};
};

export const saveAllProjectDetails = (
	analyticsEvent: UIAnalyticsEvent,
): SaveAllProjectDetailsAction => ({
	type: SAVE_ALL_PROJECT_DETAILS,
	meta: { analyticsEvent },
});

export type SaveProjectDetailsSuccessAction = {
	type: typeof SAVE_PROJECT_DETAILS_SUCCESS;
	payload: ProjectDetails;
};

export const saveProjectDetailsSuccess = (
	data: ProjectDetails,
): SaveProjectDetailsSuccessAction => ({
	type: SAVE_PROJECT_DETAILS_SUCCESS,
	payload: data,
});

export type SaveAllProjectDetailsSuccessAction = {
	type: typeof SAVE_ALL_PROJECT_DETAILS_SUCCESS;
	payload: AllProjectDetailsUpdate;
};

export const saveAllProjectDetailsSuccess = (
	data: AllProjectDetailsUpdate,
): SaveAllProjectDetailsSuccessAction => ({
	type: SAVE_ALL_PROJECT_DETAILS_SUCCESS,
	payload: data,
});

export type SaveProjectDetailsFailAction = {
	type: typeof SAVE_PROJECT_DETAILS_FAIL;
	error: Error;
};

export const saveProjectDetailsFail = (error: Error): SaveProjectDetailsFailAction => ({
	type: SAVE_PROJECT_DETAILS_FAIL,
	error,
});

export type ReloadOnRefreshAction = {
	type: typeof RELOAD_ON_REFRESH;
};

export const reloadOnRefresh = (): ReloadOnRefreshAction => ({
	type: RELOAD_ON_REFRESH,
});

export type FetchProjectCategoriesAction = {
	type: typeof FETCH_PROJECT_CATEGORIES;
};

export const fetchProjectCategories = (): FetchProjectCategoriesAction => ({
	type: FETCH_PROJECT_CATEGORIES,
});

export type FetchProjectCategoriesSuccessAction = {
	type: typeof FETCH_PROJECT_CATEGORIES_SUCCESS;
	payload: ProjectCategory[];
};

export const fetchProjectCategoriesSuccess = (
	payload: ProjectCategory[],
): FetchProjectCategoriesSuccessAction => ({
	type: FETCH_PROJECT_CATEGORIES_SUCCESS,
	payload,
});

export type FetchProjectCategoriesFailAction = {
	type: typeof FETCH_PROJECT_CATEGORIES_FAIL;
	message: {
		error: Error;
	};
};

export const fetchProjectCategoriesFail = (error: Error): FetchProjectCategoriesFailAction => ({
	type: FETCH_PROJECT_CATEGORIES_FAIL,
	message: {
		error,
	},
});

export type FetchInstancePropertiesAction = {
	type: typeof FETCH_INSTANCE_PROPERTIES;
};

export const fetchInstanceProperties = (): FetchInstancePropertiesAction => ({
	type: FETCH_INSTANCE_PROPERTIES,
});

export type FetchInstancePropertiesSuccessAction = {
	type: typeof FETCH_INSTANCE_PROPERTIES_SUCCESS;
	payload: InstanceProperties;
};

export const fetchInstancePropertiesSuccess = (
	payload: InstanceProperties,
): FetchInstancePropertiesSuccessAction => ({
	type: FETCH_INSTANCE_PROPERTIES_SUCCESS,
	payload,
});

export type FetchInstancePropertiesFailAction = {
	type: typeof FETCH_INSTANCE_PROPERTIES_FAIL;
	message: {
		error: Error;
	};
};

export const fetchInstancePropertiesFail = (error: Error): FetchInstancePropertiesFailAction => ({
	type: FETCH_INSTANCE_PROPERTIES_FAIL,
	message: {
		error,
	},
});

export type ProjectDetailsEntitiesAction =
	| FetchProjectDetailsAction
	| FetchProjectDetailsSuccessAction
	| FetchProjectDetailsFailAction
	| SaveProjectDetailsAction
	| SaveAllProjectDetailsAction
	| SaveProjectDetailsSuccessAction
	| SaveProjectDetailsFailAction
	| SaveAllProjectDetailsSuccessAction
	| ReloadOnRefreshAction
	| FetchProjectCategoriesAction
	| FetchProjectCategoriesSuccessAction
	| FetchProjectCategoriesFailAction
	| FetchInstancePropertiesAction
	| FetchInstancePropertiesSuccessAction
	| FetchInstancePropertiesFailAction;
